/**
 * FDV — engagement de la visita (ventana fantasma, resumen por página, navegador).
 *
 * Tres eventos al bus `FDV.track` (llegan a Events sin tocar el gateway):
 *   · `fdv_engagement_shadow` — simula en silencio disparadores candidatos de la ventana de
 *     correo (pausa, scroll hacia arriba más o menos sensible, tiempo, regreso a la página).
 *     No muestra nada. UN evento por disparador y por página (`regreso`: hasta 3) y un freno de
 *     20 eventos por página: el primer disparo es el que decide si la ventana habría salido.
 *   · `fdv_page_engagement` — resumen al salir de la página (puede perderse al cerrar la pestaña).
 *   · `fdv_client_info` — dispositivo, sistema, navegador y app de origen, una vez por sesión.
 *
 * Especificación y decisiones: flor_venus_comercial_intelligence,
 * 2026/10-octubre/campanas/2026-10-07_alcance-y-captacion/eventos-nuevos.md (§1–§3).
 * Reglas: sin datos personales, sin `setInterval`, nada si no hay consentimiento de analítica.
 * Expone `window.FDV.engagement` (`visibleMs`, `scrollMaxPct`) para `fdv-pdp.js`.
 */
(() => {
  const FDV = window.FDV;
  if (!FDV || typeof FDV.track !== 'function' || FDV.engagement) return;

  const V = 1;
  const PAGE_CAP = 20; // freno de seguridad: eventos de §1 por página
  const REGRESO_CAP = 3;
  const REGRESO_MIN_MS = 5000;
  const IDLE_SECS = [10, 15, 20, 30];
  const TIME_SECS = 30;
  // Misma regla de hoy (`fdv-campaign-loader.js`, #onExitIntent en celular) y dos más sensibles.
  const SCROLL_RULES = [
    { id: 'scroll_up_actual', down: 500, up: 180, ms: 350, wentDown: false },
    { id: 'scroll_up_sensible', down: 250, up: 100, ms: 500, wentDown: false },
    { id: 'scroll_up_muy_sensible', down: 150, up: 60, ms: 600, wentDown: false },
  ];
  const SESSION_INTERRUPT_KEY = 'fdv_camp_interrupted';
  const PV_KEY = 'fdv_camp_pv';
  const CLIENT_INFO_KEY = 'fdv_client_info_sent';

  // --- Reloj de página visible ----------------------------------------------
  // Milisegundos con la página visible desde la carga (el origen es el inicio de la navegación).
  let visibleAcc = 0;
  let visibleSince = document.visibilityState === 'visible' ? 0 : null;
  let hiddenAt = null;
  const visibleMs = () => visibleAcc + (visibleSince == null ? 0 : performance.now() - visibleSince);

  // --- Estado ---------------------------------------------------------------
  const fired = new Set();
  let shadowCount = 0;
  let regresos = 0;
  let timer = null;

  let lastAct = 0; // última interacción, en reloj visible
  let prevAct = null; // para el tiempo activo (ventana de 5 s por interacción)
  let activeMs = 0;
  let pauseMax = 0;
  let pauses15 = 0;
  let hides = 0;
  let fastUps = 0;
  let maxSpeed = 0;
  let flushed = false;

  let lastY = scrollPos();
  let lastT = performance.now();
  let sampleY = lastY;
  let sampleT = lastT;
  let maxReach = scrollPos() + viewHeight();

  // --- Utilidades -----------------------------------------------------------
  const allowed = () => FDV.consent?.analytics !== false;

  function send(name, props) {
    if (!allowed()) return;
    try {
      FDV.track(name, props);
    } catch (e) {
      /* el tracking nunca debe romper la página */
    }
  }

  function deviceKind() {
    return matchMedia('(max-width: 749px)').matches || 'ontouchstart' in window ? 'mobile' : 'desktop';
  }

  function store(kind, key) {
    try {
      return window[kind].getItem(key);
    } catch (e) {
      return null;
    }
  }

  // Contenedor de scroll de la página: en escritorio (≥ 990 px) el tema desplaza `.page-wrapper`
  // (la ventana no se mueve y `window.scrollY` vale 0); en celular desplaza la ventana
  // (ver `assets/scroll-container.js`).
  function scroller() {
    if (matchMedia('(min-width: 990px)').matches) {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  function scrollPos() {
    return scroller().scrollTop || 0;
  }

  function viewHeight() {
    const el = scroller();
    return el === document.scrollingElement || el === document.documentElement ? window.innerHeight : el.clientHeight;
  }

  function scrollMaxPct() {
    const h = scroller().scrollHeight;
    if (!h) return 0;
    const reach = Math.max(maxReach, scrollPos() + viewHeight());
    return Math.max(0, Math.min(100, Math.round((reach / h) * 100)));
  }

  function cartItems() {
    const n = FDV.gifts?.cart?.item_count;
    return typeof n === 'number' ? n : null;
  }

  // --- §1 Ventana fantasma --------------------------------------------------
  function emitShadow(trigger, extra) {
    if (trigger === 'regreso') {
      if (regresos >= REGRESO_CAP) return;
      regresos += 1;
    } else {
      if (fired.has(trigger)) return;
      fired.add(trigger); // se marca aunque el freno lo descarte: evita reintentos
    }
    if (shadowCount >= PAGE_CAP) return;
    shadowCount += 1;
    send('fdv_engagement_shadow', {
      trigger,
      ms_desde_carga: Math.round(visibleMs()),
      scroll_max_pct: scrollMaxPct(),
      carrito_items: cartItems(),
      ventana_mostrada_sesion: store('sessionStorage', SESSION_INTERRUPT_KEY) === '1',
      pv_acum: Number(store('localStorage', PV_KEY)) || 0,
      device: deviceKind(),
      v: V,
      ...extra,
    });
  }

  // Pausa y tiempo: un solo `setTimeout` que se reprograma a la siguiente hora posible.
  // La actividad NO reprograma (solo anota `lastAct`): al vencer, se recalcula.
  function nextDelay() {
    const v = visibleMs();
    let best = Infinity;
    if (!fired.has('time_30')) best = Math.min(best, TIME_SECS * 1000 - v);
    for (const n of IDLE_SECS) {
      if (!fired.has(`idle_${n}`)) best = Math.min(best, n * 1000 - (v - lastAct));
    }
    return best;
  }

  function schedule() {
    clearTimeout(timer);
    timer = null;
    if (document.visibilityState !== 'visible') return;
    const d = nextDelay();
    if (!Number.isFinite(d)) return;
    timer = setTimeout(tick, Math.max(d, 0) + 20);
  }

  function tick() {
    timer = null;
    if (document.visibilityState !== 'visible') return;
    const v = visibleMs();
    if (!fired.has('time_30') && v >= TIME_SECS * 1000) emitShadow('time_30');
    const idle = v - lastAct;
    for (const n of IDLE_SECS) {
      if (!fired.has(`idle_${n}`) && idle >= n * 1000) emitShadow(`idle_${n}`);
    }
    schedule();
  }

  // --- Interacción (pausas y tiempo activo) ----------------------------------
  function markActivity() {
    const v = visibleMs();
    if (v - lastAct < 50) return; // mousemove y scroll disparan muy seguido
    const gap = v - lastAct;
    if (gap > pauseMax) pauseMax = gap;
    if (gap >= 15000) pauses15 += 1;
    if (prevAct != null) activeMs += Math.min(5000, v - prevAct);
    prevAct = v;
    lastAct = v;
  }

  function onScroll(e) {
    markActivity(); // cualquier scroll cuenta como actividad (también el de un carrusel)
    // La captura recibe el scroll de TODO elemento: para las reglas solo el de la página.
    const t = e && e.target;
    if (t && t !== document && !(t instanceof Element && t.classList.contains('page-wrapper'))) return;
    const y = scrollPos();
    const now = performance.now();
    maxReach = Math.max(maxReach, y + viewHeight());

    for (const r of SCROLL_RULES) {
      if (y > lastY) {
        if (!r.wentDown && y > r.down) r.wentDown = true;
      } else if (r.wentDown && lastY - y > r.up && now - lastT < r.ms) {
        if (r.id === 'scroll_up_actual') fastUps += 1;
        emitShadow(r.id);
      }
    }

    if (now - sampleT >= 50) {
      if (y < sampleY) maxSpeed = Math.max(maxSpeed, ((sampleY - y) / (now - sampleT)) * 1000);
      sampleY = y;
      sampleT = now;
    }
    lastY = y;
    lastT = now;
  }

  // --- §2 Resumen por página --------------------------------------------------
  function flush() {
    if (flushed) return;
    flushed = true;
    const v = visibleMs();
    if (prevAct != null) activeMs += Math.min(5000, v - prevAct);
    const tailGap = v - lastAct;
    if (tailGap > pauseMax) pauseMax = tailGap;
    if (tailGap >= 15000) pauses15 += 1;
    send('fdv_page_engagement', {
      activo_ms: Math.round(activeMs),
      visible_ms: Math.round(v),
      pausa_max_ms: Math.round(pauseMax),
      pausas_15s: pauses15,
      scroll_max_pct: scrollMaxPct(),
      subida_max_px_s: Math.round(maxSpeed),
      subidas_rapidas: fastUps,
      ocultaciones: hides,
      device: deviceKind(),
      v: V,
    });
  }

  function onVisibility() {
    const now = performance.now();
    if (document.visibilityState === 'hidden') {
      if (visibleSince != null) {
        visibleAcc += now - visibleSince;
        visibleSince = null;
      }
      hiddenAt = now;
      hides += 1;
      clearTimeout(timer);
      timer = null;
      flush();
    } else {
      visibleSince = now;
      if (hiddenAt != null) {
        const away = now - hiddenAt;
        hiddenAt = null;
        if (away >= REGRESO_MIN_MS) emitShadow('regreso', { oculta_ms: Math.round(away) });
      }
      prevAct = null; // la ventana de «tiempo activo» empieza de nuevo
      schedule();
    }
  }

  // --- §3 Navegador -----------------------------------------------------------
  function clientInfo() {
    const ua = navigator.userAgent || '';
    const touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1; // iPadOS se presenta como Mac
    let os = 'otro';
    if (/iPhone|iPad|iPod/.test(ua) || touchMac) os = 'ios';
    else if (/Android/.test(ua)) os = 'android';
    else if (/Windows/.test(ua)) os = 'windows';
    else if (/Mac OS X|Macintosh/.test(ua)) os = 'macos';

    let device = 'desktop';
    if (/iPad|Tablet/.test(ua) || touchMac || (/Android/.test(ua) && !/Mobile/.test(ua))) device = 'tablet';
    else if (/Mobi|iPhone|Android/.test(ua)) device = 'mobile';

    let navegador = 'otro';
    if (/Edg\/|EdgA\/|EdgiOS\//.test(ua)) navegador = 'edge';
    else if (/SamsungBrowser/.test(ua)) navegador = 'samsung';
    else if (/Firefox\/|FxiOS\//.test(ua)) navegador = 'firefox';
    else if (/OPR\/|Opera/.test(ua)) navegador = 'otro';
    else if (/Chrome\/|CriOS\//.test(ua)) navegador = 'chrome';
    else if (/Safari\//.test(ua)) navegador = 'safari';

    let inApp = 'ninguna';
    if (/Instagram/i.test(ua)) inApp = 'instagram';
    else if (/FBAN|FBAV|FB_IAB/.test(ua)) inApp = 'facebook';
    else if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) inApp = 'tiktok';

    return {
      device,
      os,
      navegador,
      in_app: inApp,
      pantalla: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      v: V,
    };
  }

  function sendClientInfoOnce() {
    if (store('sessionStorage', CLIENT_INFO_KEY) === '1') return;
    try {
      sessionStorage.setItem(CLIENT_INFO_KEY, '1');
    } catch (e) {
      /* sin sessionStorage: se manda en cada página, sin otra consecuencia */
    }
    send('fdv_client_info', clientInfo());
  }

  // --- Arranque ---------------------------------------------------------------
  FDV.engagement = { v: V, visibleMs, scrollMaxPct };

  for (const type of ['touchstart', 'pointerdown', 'keydown', 'mousemove']) {
    window.addEventListener(type, markActivity, { passive: true });
  }
  // Captura: el scroll de `.page-wrapper` no burbujea hasta la ventana.
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flush);

  sendClientInfoOnce();
  schedule();
})();
